"use client"

import { HttpTypes } from "@medusajs/types"
import { useState } from "react"
import BlurImage from "@modules/common/components/blur-image"
import { ChevronLeft, ChevronRight } from "lucide-react"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
}

const ImageGallery = ({ images }: ImageGalleryProps) => {
  const [selectedImage, setSelectedImage] = useState(0)

  const handlePrevImage = () => {
    setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNextImage = () => {
    setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  if (!images || images.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* Main Image */}
      <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden group max-h-[70vh] sm:max-h-none">
        <BlurImage
          src={images[selectedImage]?.url || ""}
          alt={`Product image ${selectedImage + 1}`}
          fill
          className="object-cover"
          priority
        />
        
        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white p-2.5 rounded-full shadow-md opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all hover:scale-110"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white p-2.5 rounded-full shadow-md opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all hover:scale-110"
              aria-label="Next image"
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
            {selectedImage + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Gallery - Horizontal Scroll */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => setSelectedImage(idx)}
              // add rounded and relative so the inner overlay can position correctly
              className={`relative flex-shrink-0 w-20 h-20 bg-gray-100 overflow-hidden border-2 rounded-sm transition-all ${
                selectedImage === idx
                  ? 'scale-105 shadow-md'
                  : 'border-gray-200 hover:border-gray-400 opacity-70 hover:opacity-100'
              }`}
            >
              <BlurImage
                src={img.url || ""}
                alt={`View ${idx + 1}`}
                fill
                className="object-cover"
              />
              {/* Outline overlay: absolutely positioned element rendered above the image to avoid occlusion */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-sm"
                style={
                  selectedImage === idx
                    ? {
                        // draw ring inside the thumbnail so overflow:hidden won't clip it
                        boxShadow: `inset 0 0 0 2px var(--primary, #000)`,
                        zIndex: 30,
                      }
                    : { boxShadow: 'none', zIndex: 0 }
                }
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageGallery
